import React, { useEffect, useState } from 'react';
import apis from '../../utils/apis';
import httpAction from '../../utils/httpAction';
import CustomFileUploader from '../../components/common/CustomFileUploader';
import { resolveMedia } from '../../utils/media';
import { CardSkeleton, SkeletonText } from '../../components/common/Skeleton';

const fieldStyle = { width:'100%', padding:12, border:'1px solid #d0d5dd', borderRadius:10 };
const toUploader = (value='') => value ? [{ id:value, url:value, source:'server' }] : [];
const parseRows = (value, type) => String(value || '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
  const parts = line.split('|').map((item) => item.trim());
  if (type === 'values') return { title: parts[0] || '', text: parts[1] || '' };
  if (type === 'stats') return { label: parts[0] || '', value: Number(parts[1] || 0), suffix: parts[2] || '', sub: parts[3] || '' };
  return { year: parts[0] || '', title: parts[1] || '', desc: parts[2] || '' };
}).filter((item) => Object.values(item).some(Boolean));
const strRows = (list=[], type) => list.map((item) => type === 'values' ? `${item.title || ''}|${item.text || ''}` : type === 'stats' ? `${item.label || ''}|${item.value || 0}|${item.suffix || ''}|${item.sub || ''}` : `${item.year || ''}|${item.title || ''}|${item.desc || ''}`).join('\n');

export default function AboutContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [heroImage, setHeroImage] = useState([]);
  const [ceoImage, setCeoImage] = useState([]);
  const [mapImage, setMapImage] = useState([]);

  const loadData = async () => {
    setLoading(true);
    const result = await httpAction({ url: apis().publicSettings, silent: true });
    if (result?.status) {
      const s = result.settings || {};
      setForm({
        aboutTitle: s.aboutTitle || '',
        aboutDescription: s.aboutDescription || '',
        aboutStoryKicker: s.aboutStoryKicker || 'About Us',
        aboutStoryTitle: s.aboutStoryTitle || '',
        aboutStoryTextOne: s.aboutStoryTextOne || '',
        aboutStoryTextTwo: s.aboutStoryTextTwo || '',
        aboutHighlightTitle: s.aboutHighlightTitle || '',
        aboutHighlightText: s.aboutHighlightText || '',
        ceoSectionKicker: s.ceoSectionKicker || 'Leadership',
        ceoName: s.ceoName || '',
        ceoRole: s.ceoRole || '',
        ceoBioOne: s.ceoBioOne || '',
        ceoBioTwo: s.ceoBioTwo || '',
        ceoQuote: s.ceoQuote || '',
        aboutValues: strRows(s.aboutValues, 'values'),
        aboutTimeline: strRows(s.aboutTimeline, 'timeline'),
        aboutStats: strRows(s.aboutStats, 'stats'),
      });
      setHeroImage(toUploader(s.aboutHeroImage));
      setCeoImage(toUploader(s.ceoImage));
      setMapImage(toUploader(s.officeMapImage));
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      aboutHeroImage: heroImage?.[0]?.url || '',
      ceoImage: ceoImage?.[0]?.url || '',
      officeMapImage: mapImage?.[0]?.url || '',
      aboutValues: parseRows(form.aboutValues, 'values'),
      aboutTimeline: parseRows(form.aboutTimeline, 'timeline'),
      aboutStats: parseRows(form.aboutStats, 'stats'),
    };
    const result = await httpAction({ url: apis().updateSettings, method:'PUT', body: payload });
    setSaving(false);
    if (result?.status) loadData();
  };

  if (loading) return <div><SkeletonText width="220px" height={28} /><div style={{ marginTop: 18 }}><CardSkeleton count={3} height={180} /></div></div>;

  return (
    <form onSubmit={submit} style={{ display:'grid', gap:18 }}>
      <div>
        <h2 style={{ margin:0 }}>About Page CMS</h2>
        <p style={{ color:'#667085' }}>Control hero content, company story, CEO profile, map preview, stats, values, and timeline.</p>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:18 }}>
        <div style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>Hero Section</h3>
          <input style={fieldStyle} placeholder="About Title" value={form.aboutTitle || ''} onChange={(e)=>setForm({ ...form, aboutTitle:e.target.value })} />
          <textarea rows="4" style={{ ...fieldStyle, marginTop:12 }} placeholder="About Description" value={form.aboutDescription || ''} onChange={(e)=>setForm({ ...form, aboutDescription:e.target.value })} />
          <div style={{ marginTop:12 }}><CustomFileUploader multiple={false} onEdit={heroImage} onChange={setHeroImage} /></div>
          <img src={resolveMedia(heroImage?.[0]?.url)} alt="hero preview" style={{ width:'100%', height:180, objectFit:'cover', borderRadius:14, marginTop:12 }} />
        </div>
        <div style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>CEO / Chairman</h3>
          <input style={fieldStyle} placeholder="Section label" value={form.ceoSectionKicker || ''} onChange={(e)=>setForm({ ...form, ceoSectionKicker:e.target.value })} />
          <input style={{ ...fieldStyle, marginTop:12 }} placeholder="CEO Name" value={form.ceoName || ''} onChange={(e)=>setForm({ ...form, ceoName:e.target.value })} />
          <input style={{ ...fieldStyle, marginTop:12 }} placeholder="CEO Role" value={form.ceoRole || ''} onChange={(e)=>setForm({ ...form, ceoRole:e.target.value })} />
          <textarea rows="3" style={{ ...fieldStyle, marginTop:12 }} placeholder="Bio paragraph one" value={form.ceoBioOne || ''} onChange={(e)=>setForm({ ...form, ceoBioOne:e.target.value })} />
          <textarea rows="3" style={{ ...fieldStyle, marginTop:12 }} placeholder="Bio paragraph two" value={form.ceoBioTwo || ''} onChange={(e)=>setForm({ ...form, ceoBioTwo:e.target.value })} />
          <textarea rows="3" style={{ ...fieldStyle, marginTop:12 }} placeholder="Quote" value={form.ceoQuote || ''} onChange={(e)=>setForm({ ...form, ceoQuote:e.target.value })} />
          <div style={{ marginTop:12 }}><CustomFileUploader multiple={false} onEdit={ceoImage} onChange={setCeoImage} /></div>
        </div>
        <div style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>Story & Map</h3>
          <input style={fieldStyle} placeholder="Story kicker" value={form.aboutStoryKicker || ''} onChange={(e)=>setForm({ ...form, aboutStoryKicker:e.target.value })} />
          <input style={{ ...fieldStyle, marginTop:12 }} placeholder="Story title" value={form.aboutStoryTitle || ''} onChange={(e)=>setForm({ ...form, aboutStoryTitle:e.target.value })} />
          <textarea rows="3" style={{ ...fieldStyle, marginTop:12 }} placeholder="Story paragraph one" value={form.aboutStoryTextOne || ''} onChange={(e)=>setForm({ ...form, aboutStoryTextOne:e.target.value })} />
          <textarea rows="3" style={{ ...fieldStyle, marginTop:12 }} placeholder="Story paragraph two" value={form.aboutStoryTextTwo || ''} onChange={(e)=>setForm({ ...form, aboutStoryTextTwo:e.target.value })} />
          <input style={{ ...fieldStyle, marginTop:12 }} placeholder="Highlight title" value={form.aboutHighlightTitle || ''} onChange={(e)=>setForm({ ...form, aboutHighlightTitle:e.target.value })} />
          <textarea rows="2" style={{ ...fieldStyle, marginTop:12 }} placeholder="Highlight text" value={form.aboutHighlightText || ''} onChange={(e)=>setForm({ ...form, aboutHighlightText:e.target.value })} />
          <div style={{ marginTop:12 }}><CustomFileUploader multiple={false} onEdit={mapImage} onChange={setMapImage} /></div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:18 }}>
        <label style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>Values</h3>
          <textarea rows="8" style={fieldStyle} value={form.aboutValues || ''} onChange={(e)=>setForm({ ...form, aboutValues:e.target.value })} />
          <small>One per line: title|text</small>
        </label>
        <label style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>Timeline</h3>
          <textarea rows="8" style={fieldStyle} value={form.aboutTimeline || ''} onChange={(e)=>setForm({ ...form, aboutTimeline:e.target.value })} />
          <small>One per line: year|title|description</small>
        </label>
        <label style={{ background:'#fff', padding:18, borderRadius:18 }}>
          <h3>Stats</h3>
          <textarea rows="8" style={fieldStyle} value={form.aboutStats || ''} onChange={(e)=>setForm({ ...form, aboutStats:e.target.value })} />
          <small>One per line: label|value|suffix|subtext</small>
        </label>
      </div>

      <div><button type="submit" disabled={saving} style={{ padding:'14px 22px', border:'none', borderRadius:12, background:'#0f5ef7', color:'#fff', fontWeight:700 }}>{saving ? 'Saving...' : 'Save About Page'}</button></div>
    </form>
  );
}
